import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'auth/AuthProvider';
import { PERMISSIONS } from 'auth/permissions';
import { queryKeys } from 'core/api/queryKeys';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { companyPlansApi } from 'features/company-plans/companyPlans.api';
import {
  ACCESS_CODE_STATUSES,
  canRevokeAccessCode,
  companyDisplayName,
  displayedCodeValue,
  invalidateCompanyPlanQueries,
  listItems,
  listMeta,
  maskedCode,
  planDisplayName,
  revokeBlockedReasonKey,
} from 'features/company-plans/companyPlans.helpers';
import { handleMutationError, StatusChip } from 'features/company-plans/CompanyPlanUi';
import { Button } from 'shared/components/Button';
import { useConfirm } from 'shared/components/ConfirmDialog';
import { EmptyState, ErrorState, PageHeader, Skeleton } from 'shared/components/Feedback';
import { FilterBar } from 'shared/components/FilterBar';
import { ListTable } from 'shared/components/ListTable';
import { detailsAction, RowActionsCell, RowActionsHeader } from 'shared/components/RowActionsMenu';
import { Pagination } from 'shared/components/Pagination';
import { useToast } from 'shared/components/Toast';
import { SelectField, TextField } from 'shared/forms/Fields';
import { formatDateTime, formatUserLabel } from 'shared/utilities/format';

export function CodeBatchesPage() {
  const { t, locale, dir } = useI18n();
  const [filters, setFilters] = useState({ page: 1, per_page: 15, company_id: '', status: '' });
  const query = useQuery({
    queryKey: queryKeys.accessCodeBatches(filters),
    queryFn: ({ signal }) => companyPlansApi.listCodeBatches(filters, { signal }),
  });
  const items = listItems(query.data);
  const meta = listMeta(query.data);

  if (query.isLoading) return <Skeleton rows={6} />;
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;
  }

  return (
    <div>
      <PageHeader title={t('companyPlans.batchesTitle')} description={t('companyPlans.codes.inspectOnly')} />
      <FilterBar resetLabel={t('actions.reset')} onReset={() => setFilters({ page: 1, per_page: 15, company_id: '', status: '' })}>
        <TextField id="batch-company" label={t('companyPlans.companyName')} value={filters.company_id} onChange={(value) => setFilters((current) => ({ ...current, company_id: value, page: 1 }))} />
      </FilterBar>
      {items.length === 0 ? <EmptyState title={t('empty.codeBatches')} /> : (
        <ListTable caption={t('companyPlans.batchesTitle')} stickyActions>
          <thead>
            <tr>
              <th>{t('companyPlans.batchLabel')}</th>
              <th>{t('companyPlans.companyName')}</th>
              <th>{t('companyPlans.assessmentVersion')}</th>
              <th>{t('companyPlans.createdAt')}</th>
              <RowActionsHeader />
            </tr>
          </thead>
          <tbody>
            {items.map((batch) => (
              <tr key={batch.id}>
                <td>{batch.label || `#${batch.id}`}</td>
                <td>{companyDisplayName(batch.company)}</td>
                <td>{batch.version?.number ? `#${batch.version.number}` : '—'}</td>
                <td>{formatDateTime(batch.created_at, locale)}</td>
                <RowActionsCell
                  label={`${t('actions.rowActions')} #${batch.id}`}
                  items={[detailsAction(paths.companyCodeBatchDetail(batch.id), t)]}
                />
              </tr>
            ))}
          </tbody>
        </ListTable>
      )}
      <Pagination
        page={meta.current_page || filters.page}
        lastPage={meta.last_page}
        total={meta.total}
        onPageChange={(page) => setFilters((current) => ({ ...current, page }))}
        label={t('pagination.page')}
        ofLabel={t('pagination.of')}
        previousLabel={t('pagination.previous')}
        nextLabel={t('pagination.next')}
        dir={dir}
      />
    </div>
  );
}

export function CodeBatchDetailPage() {
  const { id } = useParams();
  const { t, locale } = useI18n();
  const query = useQuery({
    queryKey: queryKeys.accessCodeBatch(id),
    queryFn: ({ signal }) => companyPlansApi.showCodeBatch(id, { signal }),
  });
  if (query.isLoading) return <Skeleton rows={6} />;
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;
  }
  const batch = query.data;
  return (
    <div>
      <PageHeader title={batch.label || `${t('companyPlans.batchDetail')} #${batch.id}`} backTo={paths.companyCodeBatches} />
      <dl className="definition-grid">
        <div className="definition-row"><dt>{t('companyPlans.companyName')}</dt><dd>{companyDisplayName(batch.company)}</dd></div>
        <div className="definition-row"><dt>{t('nav.companyEntitlements')}</dt><dd>{batch.entitlement_id ? <Link to={paths.companyEntitlementDetail(batch.entitlement_id)}>#{batch.entitlement_id}</Link> : '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.assessmentVersion')}</dt><dd>{batch.version?.number ? `#${batch.version.number}` : '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.createdAt')}</dt><dd>{formatDateTime(batch.created_at, locale)}</dd></div>
      </dl>
      <p><Link to={`${paths.companyAccessCodes}?batch_id=${batch.id}`}>{t('companyPlans.codesTitle')}</Link></p>
    </div>
  );
}

export function AccessCodesPage() {
  const { t, locale, dir } = useI18n();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.codesManage);
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    page: 1,
    per_page: 15,
    company_id: '',
    batch_id: searchParams.get('batch_id') || '',
    status: '',
    participant: '',
    created_from: '',
    created_to: '',
  });
  const query = useQuery({
    queryKey: queryKeys.accessCodes(filters),
    queryFn: ({ signal }) => companyPlansApi.listAccessCodes(filters, { signal }),
  });
  const items = listItems(query.data);
  const meta = listMeta(query.data);

  const revoke = useMutation({
    mutationFn: (codeId) => companyPlansApi.revokeAccessCode(codeId),
    onSuccess: async () => {
      await invalidateCompanyPlanQueries(queryClient);
      toast.success(t('status.updated'));
    },
    onError: (error) => handleMutationError(error, { t, toast }),
  });

  if (query.isLoading) return <Skeleton rows={6} />;
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;
  }

  return (
    <div>
      <PageHeader title={t('companyPlans.codesTitle')} description={t('companyPlans.codes.inspectOnly')} />
      <p className="muted">{t('companyPlans.codes.maskedOnly')}</p>
      <FilterBar
        resetLabel={t('actions.reset')}
        onReset={() => setFilters({
          page: 1, per_page: 15, company_id: '', batch_id: '', status: '', participant: '', created_from: '', created_to: '',
        })}
      >
        <TextField id="code-company" label={t('companyPlans.companyName')} value={filters.company_id} onChange={(value) => setFilters((current) => ({ ...current, company_id: value, page: 1 }))} />
        <TextField id="code-batch" label={t('companyPlans.batchLabel')} value={filters.batch_id} onChange={(value) => setFilters((current) => ({ ...current, batch_id: value, page: 1 }))} />
        <SelectField
          id="code-status"
          label={t('companyPlans.status')}
          value={filters.status}
          onChange={(value) => setFilters((current) => ({ ...current, status: value, page: 1 }))}
          options={[{ value: '', label: t('filters.all') }, ...ACCESS_CODE_STATUSES.map((status) => ({ value: status, label: t(`companyPlans.statuses.${status}`) }))]}
        />
        <TextField id="code-participant" label={t('companyPlans.participant')} value={filters.participant} onChange={(value) => setFilters((current) => ({ ...current, participant: value, page: 1 }))} />
        <TextField id="code-from" label={t('companyPlans.dateFrom')} type="date" value={filters.created_from} onChange={(value) => setFilters((current) => ({ ...current, created_from: value, page: 1 }))} />
        <TextField id="code-to" label={t('companyPlans.dateTo')} type="date" value={filters.created_to} onChange={(value) => setFilters((current) => ({ ...current, created_to: value, page: 1 }))} />
      </FilterBar>
      {items.length === 0 ? <EmptyState title={t('empty.accessCodes')} /> : (
        <ListTable caption={t('companyPlans.codesTitle')} stickyActions>
          <thead>
            <tr>
              <th>{t('companyPlans.maskedCode')}</th>
              <th>{t('companyPlans.companyName')}</th>
              <th>{t('companyPlans.batchLabel')}</th>
              <th>{t('companyPlans.status')}</th>
              <th>{t('companyPlans.claimingParticipant')}</th>
              <th>{t('companyPlans.attempt')}</th>
              <th>{t('companyPlans.completedAt')}</th>
              <RowActionsHeader />
            </tr>
          </thead>
          <tbody>
            {items.map((code) => (
              <tr key={code.id}>
                <td className="code-masked">{maskedCode(code)}</td>
                <td>{companyDisplayName(code.company)}</td>
                <td>{code.batch?.label || code.batch_id || '—'}</td>
                <td><StatusChip status={code.status} t={t} /></td>
                <td>{formatUserLabel(code.claimed_by || code.claimed_by_user, t)}</td>
                <td>{code.attempt_id || '—'}</td>
                <td>{formatDateTime(code.completed_at, locale)}</td>
                <RowActionsCell
                  label={`${t('actions.rowActions')} #${code.id}`}
                  items={[
                    detailsAction(paths.companyAccessCodeDetail(code.id), t),
                    canManage && canRevokeAccessCode(code) ? {
                      key: 'revoke',
                      label: t('actions.revoke'),
                      tone: 'danger',
                      disabled: revoke.isPending,
                      onClick: async () => {
                        if (await confirm({ title: t('companyPlans.revokeCode'), body: t('companyPlans.revokeCodeBody') })) {
                          revoke.mutate(code.id);
                        }
                      },
                    } : canManage ? {
                      key: 'revoke-blocked',
                      label: t(revokeBlockedReasonKey(code)),
                      disabled: true,
                    } : null,
                  ]}
                />
              </tr>
            ))}
          </tbody>
        </ListTable>
      )}
      <Pagination
        page={meta.current_page || filters.page}
        lastPage={meta.last_page}
        total={meta.total}
        onPageChange={(page) => setFilters((current) => ({ ...current, page }))}
        label={t('pagination.page')}
        ofLabel={t('pagination.of')}
        previousLabel={t('pagination.previous')}
        nextLabel={t('pagination.next')}
        dir={dir}
      />
    </div>
  );
}

export function AccessCodeDetailPage() {
  const { id } = useParams();
  const { t, locale } = useI18n();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.codesManage);
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: queryKeys.accessCode(id),
    queryFn: ({ signal }) => companyPlansApi.showAccessCode(id, { signal }),
  });
  const revoke = useMutation({
    mutationFn: () => companyPlansApi.revokeAccessCode(id),
    onSuccess: async () => {
      await invalidateCompanyPlanQueries(queryClient);
      toast.success(t('status.updated'));
    },
    onError: (error) => handleMutationError(error, { t, toast }),
  });

  if (query.isLoading) return <Skeleton rows={6} />;
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;
  }
  const code = query.data;
  return (
    <div>
      <PageHeader
        title={t('companyPlans.codeDetail')}
        backTo={paths.companyAccessCodes}
        actions={canManage && canRevokeAccessCode(code) ? (
          <Button
            tone="danger"
            loading={revoke.isPending}
            onClick={async () => {
              if (await confirm({ title: t('companyPlans.revokeCode'), body: t('companyPlans.revokeCodeBody') })) {
                revoke.mutate();
              }
            }}
          >
            {t('actions.revoke')}
          </Button>
        ) : null}
      />
      {!canRevokeAccessCode(code) ? <p className="muted">{t(revokeBlockedReasonKey(code))}</p> : null}
      <dl className="definition-grid">
        <div className="definition-row"><dt>{t('companyPlans.maskedCode')}</dt><dd className="code-masked">{displayedCodeValue(code)}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.companyName')}</dt><dd>{companyDisplayName(code.company)}</dd></div>
        <div className="definition-row"><dt>{t('nav.companyPlans')}</dt><dd>{planDisplayName(code.plan || code.entitlement?.plan, locale)}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.batchLabel')}</dt><dd>{code.batch?.label || code.batch_id || '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.assessmentVersion')}</dt><dd>{code.version?.number ? `#${code.version.number}` : '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.status')}</dt><dd><StatusChip status={code.status} t={t} /></dd></div>
        <div className="definition-row"><dt>{t('companyPlans.createdAt')}</dt><dd>{formatDateTime(code.created_at, locale)}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.expiresAt')}</dt><dd>{formatDateTime(code.expires_at, locale)}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.claimingParticipant')}</dt><dd>{formatUserLabel(code.claimed_by || code.claimed_by_user, t)}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.attempt')}</dt><dd>{code.attempt_id || '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.completedAt')}</dt><dd>{formatDateTime(code.completed_at, locale)}</dd></div>
      </dl>
    </div>
  );
}
